// @ts-nocheck baseline 2026-02-16
import { useState } from "react";
import { Pressable } from "react-native";
import { PressableScale } from "pressto";
import { useRouter } from "expo-router";
import { format } from "date-fns";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { ActivityItem } from "@src/services/api/activity-api";
import { RoutinePreview } from "@src/features/alarms/components/RoutinePreview";
import { OverridePreview } from "@src/features/alarms/components/OverridePreview";
import { ResponsePreview } from "@src/features/alarms/components/ResponsePreview";
import { Icon } from "@src/components/Icon";
import { formatCameraLabel } from "@src/utils/key-parser";

function formatActivityTimestampParts(iso: string): { date: string; time: string } {
  const date = new Date(iso);
  return { date: format(date, "d MMM yy"), time: format(date, "h:mma") };
}

function getActivityRoute(item: ActivityItem): string | null {
  const cameraKey = item.cameraKey;
  if (!cameraKey) return null;

  if (item.metadata.kind === "alarm_contacts_configured") {
    const params = new URLSearchParams({
      tenantId: item.tenantId,
      siteId: item.siteId,
      cameraKey,
    });
    return `/contacts/site-alarm?${params.toString()}`;
  }

  const params = new URLSearchParams({
    siteId: item.siteId,
    cameraKey,
  });
  return `/alarm-settings?${params.toString()}`;
}

export function ActivityItemCard({ item, isLast = false }: { item: ActivityItem; isLast?: boolean }) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const { kind } = item.metadata;
  const isAlarmRoutine = kind === "alarm_routine_configured";
  const isAlarmOverride = kind === "alarm_override_configured";
  const isAlarmResponse = kind === "alarm_response_configured";
  const routine = isAlarmRoutine ? item.metadata.data.routine : null;
  const override = isAlarmOverride ? item.metadata.data.override : null;
  const response = isAlarmResponse ? item.metadata.data.response : null;
  const [showDetails, setShowDetails] = useState(false);
  const hasExpandableDetails = isAlarmRoutine || isAlarmOverride || isAlarmResponse;
  const timestamp = formatActivityTimestampParts(item.createdAt);
  const cameraKey = item.cameraKey;
  const rowRoute = getActivityRoute(item);
  const toggleDetails = () => setShowDetails((prev) => !prev);
  const handleRowPress = () => hasExpandableDetails && toggleDetails();
  const handleTitlePress = () => rowRoute && router.push(rowRoute);

  return (
    <View>
      <Pressable disabled={!hasExpandableDetails} onPress={handleRowPress}>
        <View
          flexDirection="row"
          alignItems="center"
          px="4xl"
          py="lg"
          borderBottomWidth={!isLast || showDetails ? 1 : 0}
          borderColor="borderCard"
          bg="surface"
        >
          <View flex={3} pr="md" style={{ minWidth: 0 }}>
            {rowRoute ?
              <Pressable onPress={handleTitlePress}>
                <Text textStyle="xs/medium">{item.summary}</Text>
              </Pressable>
            : <Text textStyle="xs/medium">{item.summary}</Text>}
          </View>

          <View flex={2} pr="md" style={{ minWidth: 0 }}>
            {cameraKey ?
              <PressableScale
                onPress={(event) => {
                  event.stopPropagation?.();
                  router.push(`/tenants/${item.tenantId}/sites/${item.siteId}/cameras/${cameraKey}`);
                }}
              >
                <Text textStyle="xxs/regular" color="textMuted">
                  {formatCameraLabel(cameraKey)}
                </Text>
              </PressableScale>
            : <Text textStyle="xxs/regular" color="textMuted">
                All cameras
              </Text>
            }
          </View>

          <View flex={2} pr="md" style={{ minWidth: 0, alignItems: "flex-end" }}>
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1} style={{ textAlign: "right" }}>
              {timestamp.date}
            </Text>
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1} style={{ textAlign: "right" }}>
              {timestamp.time}
            </Text>
          </View>

          <View width={24} alignItems="flex-end">
            {hasExpandableDetails ?
              <Pressable
                onPress={(event) => {
                  event.stopPropagation?.();
                  toggleDetails();
                }}
                hitSlop={8}
              >
                <Icon name={showDetails ? "chevronUp" : "chevronDown"} size={24} color={theme.colors.text} />
              </Pressable>
            : null}
          </View>
        </View>
      </Pressable>

      {showDetails ?
        <View px="4xl" py="xl" bg="surface" borderBottomWidth={isLast ? 0 : 1} borderColor="borderCard">
          <View gap="lg">
            {isAlarmRoutine ?
              <RoutinePreview routine={routine} />
            : null}

            {isAlarmOverride ?
              override ?
                <OverridePreview override={override} timezone={"UTC"} />
              : <Text textStyle="xs/regular" color="textMuted">
                  Override removed
                </Text>

            : null}

            {isAlarmResponse ?
              <ResponsePreview response={response} />
            : null}
          </View>
        </View>
      : null}
    </View>
  );
}
